import React, { useRef } from 'react'
import './oto2.css'
import oto2 from '../image/oto-2.png'
import UrgentGuarantee from '../image/urgent-guarantee-oto2.jpg'
import oto2video from '../image/oto2-video.mp4'
import oto2addtocart from '../image/oto2-addtocart.png'

/** Active step: 1, 2, or 3 (see https://leadszora.com/oto2/ ) */
const CURRENT_STEP = 2

function CheckIcon() {
  return (
    <svg
      className="oto2-circle__check"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StepStatus({ stepId, current }) {
  if (stepId < current) {
    return (
      <span className="oto2-status oto2-status--complete">✓ Completed</span>
    )
  }
  if (stepId === current) {
    return (
      <span className="oto2-status oto2-status--current">
        <span className="oto2-status__lead" aria-hidden>
          ●
        </span>
        In Progress
      </span>
    )
  }
  return (
    <span className="oto2-status oto2-status--pending">
      <span className="oto2-status__lead" aria-hidden>
        ○
      </span>
      Pending
    </span>
  )
}

const STEP_LABELS = [
  { id: 1, lines: ['Step 1: Complete'] },
  { id: 2, lines: ['Step 2: Customize', 'Order'] },
  { id: 3, lines: ['Step 3: Activate'] },
]

const OTO2_UPGRADE_INCLUDES = [
  '✔ Name, Email, Company, Phone',
  '✔ Ready-to-use formatted data',
  '✔ No messy scraping cleanup needed',
  '✔ Organized for instant usage',

]

const OTO2_WHY_EXISTS = [
  '✔ Export leads instantly (no manual copy-paste)',
  '✔ Use data in your own systems',
  '✔ Save hours of formatting & cleanup',
  '✔ Make your workflow faster & scalable',
  '✔ Perfect for agencies & serious users',

]

const OTO2_USE_CASES = [
  '✔ Export to Google Sheets',
  '✔ Upload into CRM (HubSpot, Zoho, etc.)',
  '✔ Run email campaigns',
  '✔ Share with clients or team',
  '✔ Build your own lead database',

]

const OTO2_MEANS = [
  'Your standard OTO 2 access to up to 10,000 company records',
  'PLUS an extra 10,000 contact credits added to your account',
  'No additional setup required — credits apply automatically',
]
const OTO2_MEAN = [
  '✔ Advanced CSV Export (Clean & Structured)',
  '✔ Google Sheets Ready Format',
  '✔ CRM-Ready Data (HubSpot / Zoho Compatible)',
  '✔ Bulk Export Options',
  '✔ Organized Lead Data (No Cleanup Needed)',
  '✔ Works Perfectly With Your Outreach System',

]


function ListCheckIcon() {
  return (
    <svg
      className="oto2-value__check"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DiamondIcon() {
  return (
    <svg
      className="oto2-value-card__diamond"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l8 10-8 10-8-10 8-10z" />
    </svg>
  )
}

function GiftIcon() {
  return (
    <svg
      className="oto2-bonus__gift"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2 2 0 00-2-2c-1.66 0-3 1.34-3 3h-2c0-1.66-1.34-3-3-3a2 2 0 00-2 2c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1h-2V5h2zm-4 0c.55 0 1 .45 1 1s-.45 1-1 1H8V5h3zm8 14H4v-4h16v4zm0-6H4V8h16v4z" />
    </svg>
  )
}

function VolumeIcon() {
  return (
    <svg
      className="oto2-offer-media__volume"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M11 5L6 9H3v6h3l5 4V5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M15.54 8.46a5 5 0 010 7.07M17.66 6.34a8 8 0 010 11.32"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default function Oto2() {
  const cartSectionRef = useRef(null)

  const scrollToCartSection = () => {
    cartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <section className="oto2-stepper-section" aria-label="OTO2 setup progress">
        <div className="oto2-stepper-inner">
          <div
            className="oto2-stepper"
            data-current-step={String(CURRENT_STEP)}
          >
            <div className="oto2-circles-row" role="list">
              {STEP_LABELS.map((step) => {
                const isComplete = step.id < CURRENT_STEP
                const isCurrent = step.id === CURRENT_STEP

                let circleClass = 'oto2-circle'
                if (isComplete) circleClass += ' oto2-circle--complete'
                else if (isCurrent) circleClass += ' oto2-circle--current'
                else circleClass += ' oto2-circle--pending'

                return (
                  <div key={step.id} className="oto2-circle-col" role="listitem">
                    <div
                      className={circleClass}
                      aria-current={isCurrent ? 'step' : undefined}
                    >
                      {isComplete ? (
                        <CheckIcon />
                      ) : (
                        <>
                          {step.id}
                          {isCurrent && step.id === 2 ? (
                            <span className="oto2-circle__badge" aria-hidden />
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="oto2-labels-row">
              {STEP_LABELS.map((step) => {
                const isComplete = step.id < CURRENT_STEP
                const isCurrent = step.id === CURRENT_STEP
                let boxClass = 'oto2-label-box'
                if (isComplete) boxClass += ' oto2-label-box--complete'
                else if (isCurrent) boxClass += ' oto2-label-box--current'
                else boxClass += ' oto2-label-box--pending'

                return (
                  <div key={step.id} className="oto2-label-col">
                    <div className={boxClass}>
                      {step.lines.map((line, i) => (
                        <React.Fragment key={line}>
                          {i > 0 ? <br /> : null}
                          {line}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="oto2-status-row">
              {STEP_LABELS.map((step) => (
                <div key={step.id} className="oto2-status-col">
                  <StepStatus stepId={step.id} current={CURRENT_STEP} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="oto2-offer"
        aria-labelledby="oto2-offer-heading"
      >
        <div className="oto2-offer__inner">
          <h2 id="oto2-offer-heading" className="oto2-offer__title">

            <span className="oto2-offer__accent">Unlock Advanced Data, Exports & CRM Integration Power
            </span>
          </h2>
          <p className="oto2-offer__sub">
            Upgrade to the Advanced Research Suite and Access clean, structured lead data + export to Google Sheets, CSV & CRM tools — so you can use your leads anywhere — so you can find higher-quality leads and make better decisions.
          </p>

          <div className="oto2-offer-media">


            {/* <img
                className="oto2-offer-media__poster"
                src={oto2}
                alt="OTO 2"
                width={1200}
                height={675}
              /> */}
            <video
              className="jv-video-frame__player"
              controls
              playsInline
              preload="metadata"
              poster={oto2}
              src={oto2video}
              title="Demo: high-converting funnel and system"
            >
              Your browser does not support the video tag.
            </video>

          </div>

          <button type="button" className="oto2-offer-cta" onClick={scrollToCartSection}>
            <span className="oto2-offer-cta__primary">
              Yes! Unlock Data Export & CRM Toolkit ! »
            </span>
            <span className="oto2-offer-cta__secondary">
              Yes, Add Advanced Research Suite To My Order
            </span>
          </button>

          <div className="oto2-offer-pricing">
            <div className="oto2-offer-pricing__head">
              <p className="oto2-offer-pricing__label">One-Time Upgrade</p>
              <p className="oto2-offer-pricing__price">$59</p>
            </div>
            <ul className="oto2-offer-pricing__list">
              <li>Up to 10,000 company records</li>
              <li>Advanced Export + CRM Integration Toolkit</li>
            </ul>
            <div className="oto2-offer-pricing__meta">
              <span className="oto2-offer-pricing__tag">No Subscription</span>
              <span className="oto2-offer-pricing__tag">No Hidden Fees</span>
            </div>
            <p className="oto2-offer-pricing__note">Instant access after checkout</p>
          </div>

          <p className="oto2-offer__foot-tag">IF PURCHASED SEPARATELY</p>
          <p className="oto2-offer__foot-note">
            yours today for a one-time $59.
          </p>
        </div>
      </section>

      <section
        className="oto2-value"
        aria-labelledby="oto2-value-heading"
      >
        <div className="oto2-value__inner">
          <h2 id="oto2-value-heading" className="oto2-value__title">
            Go Beyond Just Finding Leads —
            {' '}
            <span className="oto2-value__yellow">Use Them Anywhere You Want
            </span>
          </h2>
          <p className="oto2-value__lead">
            Export, organize, and connect your leads with your favorite tools — no limitations

          </p>

          <div className="oto2-value-columns">
            <article className="oto2-value-card oto2-value-card--yellow">
              <h3 className="oto2-value-card__title">
                <DiamondIcon />
                Access Clean & Structured Lead Data
              </h3>
              <ul className="oto2-value-list">
                {OTO2_UPGRADE_INCLUDES.map((t) => (
                  <li key={t}>
                    <ListCheckIcon />
                    {t}
                  </li>
                ))}
              </ul>
            </article>
            <article className="oto2-value-card oto2-value-card--blue">
              <h3 className="oto2-value-card__title">
                <DiamondIcon />
                Why This Upgrade Matters:
              </h3>
              <ul className="oto2-value-list">
                {OTO2_WHY_EXISTS.map((t) => (
                  <li key={t}>
                    <ListCheckIcon />
                    {t}
                  </li>
                ))}
              </ul>
            </article>
            <article className="oto2-value-card oto2-value-card--blue">
              <h3 className="oto2-value-card__title">
                <DiamondIcon />
                Use Your Leads Anywhere:
              </h3>
              <ul className="oto2-value-list">
                {OTO2_USE_CASES.map((t) => (
                  <li key={t}>
                    <ListCheckIcon />
                    {t}
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="oto2-bonus">
            <div className="oto2-bonus__head">
              <div className="oto2-bonus__head-main">
                <GiftIcon />
                <div>
                  <p className="oto2-bonus__head-title">
                    What You Get With This Upgrade:
                  </p>
                  <p className="oto2-bonus__head-sub">
                    Special early-access benefit for first users
                  </p>
                </div>
              </div>
              <span className="oto2-bonus__pill">
                <span aria-hidden>👤</span> FIRST 50 USERS ONLY
              </span>
            </div>
            <p className="oto2-bonus__intro">
              As part of our initial launch phase, we&apos;re offering a limited
              bonus to early adopters of Advanced Prospect Suite.
            </p>

            <div className="oto2-bonus-compare">
              <div className="oto2-bonus-compare__card">
                <p className="oto2-bonus-compare__label">Standard OTO 2 Access</p>
                <ul className="oto2-bonus-compare__list">
                  <li>
                    <ListCheckIcon />
                    Up to 10,000 company records
                  </li>
                </ul>
              </div>
              <div className="oto2-bonus-compare__plus" aria-hidden>
                +
              </div>
              <div className="oto2-bonus-compare__card oto2-bonus-compare__card--bonus">
                <span className="oto2-bonus-compare__ribbon">BONUS</span>
                <p className="oto2-bonus-compare__label">Extra Contact Credits</p>
                <p className="oto2-bonus-compare__highlight">
                  +10,000 contact credits added to your account
                </p>
              </div>
              <div className="oto2-bonus-compare__result">
                <p className="oto2-bonus-compare__result-title">Total You Can Use Instantly</p>
                <p className="oto2-bonus-compare__result-value">20,000 Contact Credits</p>
              </div>
            </div>

            <div className="oto2-bonus-mean">
              <p className="oto2-bonus-mean__title">What This Means For You</p>
              <ul className="oto2-value-list oto2-value-list--tight">
                {OTO2_MEANS.map((t) => (
                  <li key={t}>
                    <ListCheckIcon />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="oto2-bonus-mean">

              <ul className="oto2-value-list oto2-value-list--tight">
                {OTO2_MEAN.map((t) => (
                  <li key={t}>
                    <ListCheckIcon />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="oto2-bonus-note">
              <div className="oto2-bonus-note__col">
                <p className="oto2-bonus-note__label">Limited Time</p>
                <p className="oto2-bonus-note__text">
                  Available only while the first 50 upgrade slots remain.
                </p>
              </div>
              <div className="oto2-bonus-note__col">
                <p className="oto2-bonus-note__label">First Come Basis</p>
                <p className="oto2-bonus-note__text">
                  Not guaranteed once the limit is reached.
                </p>
              </div>
              <div className="oto2-bonus-note__col">
                <p className="oto2-bonus-note__label">Optional</p>
                <p className="oto2-bonus-note__text">
                  OTO 2 works fully without this bonus as well.
                </p>
              </div>
            </div>

            <p className="oto2-bonus__spots">
              <span aria-hidden>🕒</span> Limited Spots Remaining: 50
            </p>
          </div>

          <div className="oto2-cart" ref={cartSectionRef}>
            <div className="oto2-get-cta__cart-btn">
              <a
                className="oto2-get-cta__cart-btn-img"
                href="https://warriorplus.com/o2/buy/sd90jm/n9028j/fs5bhj"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={oto2addtocart} alt="Add to Cart" />
              </a>
            </div>
            </div>
            <p className="oto2-pricing-pro__disclaimer">
              <em>WarriorPlus is used to help manage the sale of products on this site. While WarriorPlus helps facilitate the sale, all payments are made directly to the product vendor and NOT WarriorPlus. Thus, all product questions, support inquiries and/or refund requests must be sent to the vendor. WarriorPlus's role should not be construed as an endorsement, approval or review of these products or any claim, statement or opinion used in the marketing of these products.</em>
            </p>
            <p className="oto2-cart__foot">
              <span className="oto2-value__yellow">
                Advanced Prospect Suite + Export Unlimited Leads + Use Anywhere

              </span>
            </p>
            <p className="oto2-cart__instant">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
              Instant Access • Complete Contact Data
            </p>

          <div className="oto2-compliance" role="note">
            <svg
              className="oto2-compliance__shield"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
            </svg>
            <p className="oto2-compliance__text">
              <strong>Compliance Statement:</strong> Data is sourced from publicly
              available business records and directories. Use in accordance with
              applicable laws and platform terms.
            </p>
          </div>
        </div>
      </section>

      <section
        className="oto2-urgent"
        aria-labelledby="oto2-urgent-hero"
      >
        <div className="oto2-urgent__inner">
          <p className="oto2-urgent__updated">
            <span className="oto2-urgent__dot" aria-hidden />
            Updated 3 minutes ago
          </p>

          <div
            className="oto2-urgent__track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={80}
            aria-label="Offer availability"
          >
            <div className="oto2-urgent__fill">
              <span className="oto2-urgent__fill-text">
                This Upgrade Is Only Available During Checkout — Don’t Miss It
              </span>
            </div>
          </div>

          <div className="oto2-urgent__hero" id="oto2-urgent-hero">
            Want To Actually Use Your Leads — Not Just Collect Them?

          </div>

          <p className="oto2-urgent__sub">
            This is an optional upgrade available during checkout.
          </p>
          <p className="oto2-urgent__bold-line">
            One-time upgrade available during checkout.
          </p>

          <div className="oto2-urgent__spots">
            <span className="oto2-urgent__spots-badge">LIMITED SPOTS</span>
            <p className="oto2-urgent__spots-text">
              Stop being stuck inside one platform — export your leads, use them anywhere, and scale your outreach easily

            </p>
          </div>

          <p className="oto2-urgent__warn" role="status">
            {/* <span className="oto2-urgent__warn-icon" aria-hidden>
              ⚠️
            </span> */}
            <span>
              <strong className="oto2-urgent__warn-lead">Important:</strong> This
              Offer Could Be Removed At Any Time, So Please Click The Button To
              Join Us Now!
            </span>
          </p>

          <a
            className="oto2-urgent__cta"
            onClick={scrollToCartSection}
          >
            <span className="oto2-urgent__cta-top">
              {/* <svg
              className="oto2-urgent__cta-cart"
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.15.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg> */}
              Yes — Give Me Full Control Over My Lead Data

            </span>
            <span className="oto2-urgent__cta-sub">
              Yes – I Want To Generate MORE SALES &amp; COMMISSIONS Daily!
            </span>
          </a>

          <div className="oto2-urgent__trust-shell">
            <div className="oto2-urgent__trust-inner">
              
              <p className="oto2-urgent__secure">
                <span aria-hidden>🔒</span> Secure Payment • 100% Safe • Encrypted
                Checkout
              </p>
            </div>
          </div>
        </div>
        <div className="oto2-urgent__disclaimer">
          <p>
            <em>
              No risk — try the export system and integrations for 7 days

            </em>
          </p>
        </div>
      </section>

      <section
        className="oto2-guarantee-block"
        aria-labelledby="oto2-guarantee-heading"
      >
        <div className="oto2-guarantee-block__inner">
          <p className="oto2-guarantee-block__badge">
            <span aria-hidden>🛡️</span> 100% RISK-FREE GUARANTEE{' '}
            <span aria-hidden>🛡️</span>
          </p>
          <h2 id="oto2-guarantee-heading" className="oto2-guarantee-block__title">
            7-Day Money-Back Guarantee
          </h2>
          <p className="oto2-guarantee-block__lead">
            This upgrade is covered by our 7-day money-back guarantee.
          </p>

          <div className="oto2-guarantee-block__grid">
            <div className="oto2-guarantee-block__media">
              <img
                className="oto2-guarantee-block__img"
                src={UrgentGuarantee}
                alt="Advanced Prospect Suite dashboard preview"
              />
            </div>
            <div className="oto2-guarantee-block__cards">
              <article className="oto2-guarantee-card">
                <div className="oto2-guarantee-card__head">
                  <span className="oto2-guarantee-card__icon" aria-hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.16-1.62-4.16-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.73 1.36 4.73 4.08c-.01 1.77-1.29 2.99-3.01 3.28z" />
                    </svg>
                  </span>
                  <h3 className="oto2-guarantee-card__title">Full Refund Within 7 Days</h3>
                </div>
                <p className="oto2-guarantee-card__text">
                  If Advanced Prospect Suite isn&apos;t the right fit for you, you
                  may request a refund within 7 days of purchase, in accordance
                  with our refund policy.
                </p>
              </article>
              <article className="oto2-guarantee-card">
                <div className="oto2-guarantee-card__head">
                  <span
                    className="oto2-guarantee-card__icon oto2-guarantee-card__icon--warn"
                    aria-hidden
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                  </span>
                  <h3 className="oto2-guarantee-card__title">Important Note</h3>
                </div>
                <p className="oto2-guarantee-card__text">
                  Refund requests must be submitted through the appropriate support
                  channel and are subject to standard review.
                </p>
              </article>
              <article className="oto2-guarantee-card oto2-guarantee-card--support">
                <div className="oto2-guarantee-card__head">
                  <span
                    className="oto2-guarantee-card__icon oto2-guarantee-card__icon--mail"
                    aria-hidden
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                  </span>
                  <h3 className="oto2-guarantee-card__title">
                    Need Help or Have Questions?
                  </h3>
                </div>
                <div className="oto2-guarantee-card__support-row">
                  <p className="oto2-guarantee-card__text oto2-guarantee-card__text--support">
                    Our support team is here to assist you.
                  </p>
                  <a
                    className="oto2-guarantee-card__btn"
                    href="mailto:support@leadszora.com"
                  >
                    Contact Support
                  </a>
                </div>
                <p className="oto2-guarantee-card__email">
                  Email us at:{' '}
                  <a href="mailto:support@leadszora.com">support@leadszora.com</a>
                </p>
              </article>
            </div>
          </div>

          {/* <div className="oto2-guarantee-block__disclaimer">
            <p>
              <strong>Disclaimer:</strong>{' '}
              <em>
                WarriorPlus is used as a secure payment processor only. For
                product support, billing, or refund requests related to this
                purchase, please contact the vendor (Leadszora) directly through the
                support channels listed on your receipt or this page.
              </em>
            </p>
          </div> */}
        </div>
      </section>

      <footer className="oto1-site-footer">
        <div className="oto1-site-footer__inner">
          <p className="oto1-site-footer__copy">
            © {new Date().getFullYear()} Leadszora. All Rights Reserved.
          </p>
          <nav className="oto1-site-footer__nav" aria-label="Footer">
            <a href="/2.0privacy-policy">Privacy Policy</a>
            <span className="oto1-site-footer__sep" aria-hidden>
              |
            </span>
            <a href="/2.0Terms-of-Service">Terms &amp; Conditions</a>
            <span className="oto1-site-footer__sep" aria-hidden>
              |
            </span>
            <a href="/2.0Earning-Disclaimer">Earning Disclaimer</a>
            <span className="oto1-site-footer__sep" aria-hidden>
              |
            </span>
            <a href="/2.0Contact-Us">Contact Us</a>
          </nav>
        </div>
      </footer>
    </>
  )
}
